import api from '../../services/api';
import transactionService from '../../services/transactionService';
import autoMatchService from '../../services/autoMatchService';

/**
 * KC-DO (#63): the client half of "Do it for me".
 *
 * The backend's propose_action tool only OFFERS — this registry is what a
 * click actually runs, and every executor goes through the exact endpoints
 * the equivalent UI button uses, under the logged-in user's own auth. KC
 * never supplies IDs or amounts: executors re-derive the target set fresh at
 * click time, so a stale offer simply finds less (or nothing) to do.
 *
 * Guardrail carried from the platform: capital rows (Account_Capital —
 * deposits/withdrawals) are filtered out unconditionally. They mint NAV units
 * and stay human-approved forever.
 *
 * Each executor resolves to { message, undoToken? }. A non-null undoToken
 * means the action can be reversed via undoKcAction(key, token) — the UI
 * offers an Undo link after success. Executors throw on failure; the caller
 * renders the error.
 */
function makeTradeFix(successMessage) {
  return {
    async execute(params) {
      // Slice 2: a batch offer carries params.items (one entry per trade,
      // each verified server-side); a single offer carries the fields flat.
      const items = Array.isArray(params?.items) ? params.items : (params?.tradeId ? [params] : []);
      if (!items.length || items.some(i => !i.tradeId || !i.updates)) {
        throw new Error('Missing fix parameters — ask the Coach to propose it again.');
      }
      for (const i of items) await api.put(`/trades/${i.tradeId}`, i.updates);
      const undo = items.filter(i => i.undo).map(i => ({ tradeId: i.tradeId, updates: i.undo }));
      return {
        message: items.length === 1 ? successMessage : `${successMessage} (${items.length} trades)`,
        undoToken: undo.length ? (undo.length === 1 && items.length === 1 ? undo[0] : { items: undo }) : null
      };
    },
    async undo(token) {
      const items = Array.isArray(token?.items) ? token.items : [token];
      for (const t of items) await api.put(`/trades/${t.tradeId}`, t.updates);
      return { message: items.length === 1 ? 'Reverted to the previous value.' : `Reverted ${items.length} trades to their previous values.` };
    }
  };
}

const KC_ACTION_EXECUTORS = {
  approve_portfolio_rows: {
    async execute() {
      const data = await transactionService.getPortfolioTransactions();
      const rows = data?.data?.transactions || [];
      const ids = rows
        .filter((t) => t.category !== 'Account_Capital'
          && (t.recon_status === 'Unmatched' || t.recon_status === 'Review'))
        .map((t) => t.id);
      if (!ids.length) {
        return { message: 'Nothing left to approve — the queue was already clear.' };
      }
      const res = await transactionService.bulkApprove(ids);
      const n = res?.data?.updated ?? ids.length;
      return { message: `Done — ${n} row${n === 1 ? '' : 's'} approved. Deposits and withdrawals were not touched.` };
    }
  },

  run_auto_match: {
    async execute() {
      const data = await autoMatchService.apply('all');
      const created = data?.created ?? 0;
      const linked = data?.linked ?? 0;
      const deferred = data?.deferred ?? 0;
      const runId = data?.runId || null;
      const bits = [];
      if (created) bits.push(`${created} trade${created === 1 ? '' : 's'} created`);
      if (linked) bits.push(`${linked} closing fill${linked === 1 ? '' : 's'} linked`);
      if (!bits.length) bits.push('nothing new to place');
      return {
        message: `Auto-Match ran: ${bits.join(', ')}${deferred ? ` — ${deferred} left for you to review` : ''}.`,
        undoToken: runId
      };
    },
    async undo(runId) {
      await autoMatchService.undoRun(runId);
      return { message: 'Auto-Match run undone — everything it created is removed.' };
    }
  },

  risk_backfill: {
    async execute() {
      const res = await api.post('/trade-settings/risk-backfill', { scope: 'all' });
      const d = res.data?.data || {};
      if (d.background) {
        return { message: res.data?.message || `Applying default R to ${d.eligible} trades in the background — they’ll update shortly.` };
      }
      return { message: `Done — default R applied to ${d.updated ?? 0} trade${(d.updated ?? 0) === 1 ? '' : 's'}.` };
    }
  },

  // ── #65 per-trade fixes ─────────────────────────────────────────────────────
  // params arrive from the backend's propose_action offer — the fix values
  // were derived server-side from the trade's own broker records; nothing
  // here is model-authored. All three PUT fixes share one shape and undo by
  // PUTting the prior value back (a direction revert re-triggers the same
  // recompute path).
  fix_trade_currency: makeTradeFix('Currency fixed — NAV is recalculating from the trade\'s entry date.'),
  fix_trade_direction: makeTradeFix('Direction flipped — the P&L has been recomputed from the broker fills.'),
  fix_trade_quantity: makeTradeFix('Quantity corrected — position value and risk have recomputed.'),

  delete_duplicate_trade: {
    async execute(params) {
      const items = Array.isArray(params?.items) ? params.items : (params?.tradeId ? [params] : []);
      if (!items.length || items.some(i => !i.tradeId)) throw new Error('Missing fix parameters — ask the Coach to propose it again.');
      for (const i of items) await api.delete(`/trades/${i.tradeId}`);
      return { message: items.length === 1 ? 'Duplicate deleted — the broker-backed original is kept.' : `${items.length} duplicates deleted — the broker-backed originals are kept.` };
    }
  },

  draft_weekly_reflection: {
    async execute(params = {}) {
      const res = await api.post('/journals/draft-weekly', params?.week_ending ? { week_ending: params.week_ending } : {});
      const d = res.data?.data || {};
      if (d.skipped) return { message: d.reason === 'nothing closed this week' ? 'Nothing closed this week, so there was nothing to draft from.' : 'Could not draft right now.' };
      return { message: `Draft is in your journal — edit it and keep it, or delete it. It is not yours until you save it. Open Mind & Markets → Journal.` };
    }
  },

  // ── Slice 2: answers only the user has ─────────────────────────────────────
  set_default_risk: {
    async execute(params) {
      if (!params?.mode || !(params.value > 0)) throw new Error('Missing parameters — ask the Coach to propose it again.');
      await api.put('/user/preferences/risk-defaults', { default_risk_mode: params.mode, default_risk_value: params.value });
      let tail = '';
      if (params.backfill) {
        const res = await api.post('/trade-settings/risk-backfill', { scope: 'all' });
        const d = res.data?.data || {};
        tail = d.background ? ' R is being filled in on the trades that had none.' : ` R filled in on ${d.updated ?? 0} trade${(d.updated ?? 0) === 1 ? '' : 's'}.`;
      }
      const words = params.mode === 'entry_pct' ? `${params.value}% from entry` : params.mode === 'fum_pct' ? `${params.value}% of FUM` : `$${params.value} fixed`;
      return {
        message: `Default risk set to ${words}.${tail}`,
        undoToken: params.previousMode ? { mode: params.previousMode, value: params.previousValue } : { clear: true }
      };
    },
    async undo(token) {
      await api.put('/user/preferences/risk-defaults', token.clear ? { default_risk_mode: null, default_risk_value: null } : { default_risk_mode: token.mode, default_risk_value: token.value });
      return { message: 'Default risk put back the way it was. Any R that was filled in stays — it only ever filled blanks.' };
    }
  },

  // Retired 28 Oct 2026: starting cash comes from the broker's file, never a
  // typed figure. Kept so a button from an older chat can still be undone.
  add_opening_balance: {
    async execute() {
      throw new Error('Kodahfy works out starting cash from your broker file now. Import the older file instead.');
    },
    async undo(token) {
      await api.put(`/accounts/${token.accountId}`, { opening_balance: '' });
      return { message: 'Opening balance removed again.' };
    }
  },

  // Retired 3 Nov 2026 (item 4b): an account a broker's file feeds takes no
  // typed buys or sells. Kept so a button from an older chat says so.
  declare_opening_position: {
    async execute() {
      throw new Error("Kodahfy doesn't take a typed opening position any more. Import the older file from your broker that holds the purchase.");
    }
  },

  // ── #68 guard — opening balance made redundant by a later import ────────────
  // Clearing goes through the same account endpoint as blanking the field in
  // Settings → Accounts, which unwinds the underlying entry (and its units)
  // cleanly. Undo re-sets the prior amount/date; the backend re-routes it to
  // the right treatment exactly as if the user typed it again.
  // Repairs against the broker's own statement (1 Oct 2026). Each runs the
  // same endpoint the Trades / Reconciliation screens use and undoes
  // through the activity ledger's own undo.
  close_stale_open: {
    async run(params) {
      const res = await api.post(`/trades/${params.tradeId}/close-outside`, { actor: 'coach' });
      const d = res.data?.data || {};
      return {
        message: `Closed as at ${d.date}${d.price_source === 'entry_price' ? ' at cost — the sale price was not known, so no profit is booked' : " at the broker's last price"}. ${d.why || ''}`,
        undoToken: { tradeId: params.tradeId }
      };
    },
    async undo(token) {
      await api.delete(`/trades/${token.tradeId}/close-outside`);
      return { message: 'Reopened — the close-out is removed.' };
    }
  },
  unlink_exit: {
    async run(params) {
      const res = await api.post(`/trades/${params.tradeId}/unlink-exits`, { transactionIds: params.transactionIds, actor: 'coach' });
      const d = res.data?.data || {};
      return {
        message: `${d.unlinkedCount} sale${d.unlinkedCount === 1 ? '' : 's'} unmatched — back on the Reconciliation page for Auto-Match to place.`,
        undoToken: { tradeId: params.tradeId, transactionIds: params.transactionIds }
      };
    },
    async undo(token) {
      for (const id of token.transactionIds || []) await api.post(`/trades/${token.tradeId}/link-exit`, { transactionId: id });
      return { message: 'Matched back to the same trade.' };
    }
  },
  rebuild_book: {
    async run(params) {
      const res = await api.post('/nav/rebuild-book', { accountId: params.accountId, actor: 'coach' });
      const d = res.data?.data || {};
      return { message: d.message || `Rebuilding ${d.account || 'the account'} from the broker's record.`, undoToken: null };
    }
  },
  record_broker_holding: {
    async run(params) {
      const res = await api.post('/nav/broker-holdings/record', { accountId: params.accountId, symbol: params.symbol, actor: 'coach' });
      const d = res.data?.data || {};
      return {
        message: `${params.symbol} recorded as held on ${d.as_of} at ${d.cost_source === 'broker' ? "the broker's cost" : "the broker's price that day"}. ${d.why || ''}`,
        undoToken: d.transaction_id ? { transactionId: d.transaction_id } : null
      };
    },
    async undo(token) {
      await api.delete(`/nav/opening-positions/${token.transactionId}`);
      return { message: 'Removed — the holding is no longer recorded.' };
    }
  },

  remove_opening_balance: {
    async execute(params) {
      if (!params?.accountId) throw new Error('Missing fix parameters — ask the Coach to propose it again.');
      await api.put(`/accounts/${params.accountId}`, { opening_balance: '' });
      return {
        message: 'Opening balance removed — cash balances and portfolio value now come from the imported transactions alone.',
        undoToken: params.undo ? { accountId: params.accountId, updates: params.undo } : null
      };
    },
    async undo(token) {
      await api.put(`/accounts/${token.accountId}`, { ...token.updates, restore: true });
      return { message: 'Opening balance restored to the previous amount.' };
    }
  },

  // ── Matching setup (audit item 2, 11 Sep 2026) ──────────────────────────────
  // Same consent shape: the coach showed the plan, the click is the yes.
  setup_automation: {
    async execute() {
      const res = await api.post('/automation/setup');
      const d = res.data?.data || {};
      const pass = d.pass || {};
      const bits = [];
      if (pass.approved) bits.push(`filed ${pass.approved} cash item${pass.approved === 1 ? '' : 's'}`);
      const matched = (pass.created || 0) + (pass.linked || 0);
      if (matched) bits.push(`matched ${matched} transaction${matched === 1 ? '' : 's'} into trades`);
      return {
        message: bits.length
          ? `Kodahfy is set up and ${bits.join(' and ')} straight away${pass.remaining ? ` — ${pass.remaining} still need your eye` : ''}. Everything is undoable from Settings → Automation.`
          : 'Kodahfy is set up. It runs after every import and every Sunday.'
      };
    }
  },

  set_default_system: {
    async execute(params) {
      if (!params?.tradingSystem) throw new Error('Missing parameters — ask the Coach to propose it again.');
      await autoMatchService.createRule({ match_type: 'default', trading_system: params.tradingSystem });
      if (!params.previousEnabled) await autoMatchService.setEnabled(true);
      let runId = null;
      let tail = '';
      if (params.runAfter) {
        const data = await autoMatchService.apply('all');
        runId = data?.runId || null;
        const created = data?.created ?? 0, linked = data?.linked ?? 0, deferred = data?.deferred ?? 0;
        tail = ` Auto-Match ran: ${created} trade${created === 1 ? '' : 's'} created, ${linked} closing fill${linked === 1 ? '' : 's'} linked${deferred ? `, ${deferred} left for you` : ''}.`;
      }
      return {
        message: `"${params.tradingSystem}" is now the main system${params.previousEnabled ? '' : ' and Auto-Match is on'}.${tail}`,
        undoToken: { previousSystem: params.previousSystem || null, previousEnabled: !!params.previousEnabled, runId }
      };
    },
    async undo(token) {
      if (token.runId) await autoMatchService.undoRun(token.runId);
      await autoMatchService.createRule({ match_type: 'default', trading_system: token.previousSystem || null });
      if (!token.previousEnabled) await autoMatchService.setEnabled(false);
      return { message: 'Put back the way it was — the run is undone and the previous main system restored.' };
    }
  },

  declare_account_type: {
    async execute(params) {
      if (!params?.accountId || !params?.instrumentType || !params?.assetType) throw new Error('Missing parameters — ask the Coach to propose it again.');
      let ruleId = params.ruleId || null;
      let createdRule = false;
      if (ruleId) {
        await autoMatchService.updateRule(ruleId, { instrument_type: params.instrumentType });
      } else {
        const rule = await autoMatchService.createRule({ match_type: 'rule', match_value: params.accountId, instrument_type: params.instrumentType, trading_system: params.system });
        ruleId = rule?.id || null;
        createdRule = true;
      }
      const res = await api.post('/trades/bulk-asset-type', { accountId: params.accountId, assetType: params.assetType });
      const d = res.data?.data || {};
      return {
        message: `${params.accountName} now declares ${params.assetType}${d.updated ? `, and ${d.updated} existing trade${d.updated === 1 ? '' : 's'} got the label` : ''}. Future fills from it are typed automatically.`,
        undoToken: { ruleId, createdRule, ids: d.ids || [] }
      };
    },
    async undo(token) {
      if (token.ids?.length) await api.post('/trades/bulk-asset-type', { tradeIds: token.ids, clear: true });
      if (token.createdRule && token.ruleId) await autoMatchService.deleteRule(token.ruleId);
      else if (token.ruleId) await autoMatchService.updateRule(token.ruleId, { instrument_type: null });
      return { message: 'Declaration removed and those trades are untyped again.' };
    }
  },

  retry_ib_sync: {
    async execute() {
      const res = await api.post('/integrations/ib/sync');
      if (res.status === 202 || res.data?.backgrounded || res.data?.data?.backgrounded) {
        return { message: 'Sync started — it’s a big one, so it’s finishing in the background. The Settings → Accounts page shows its status.' };
      }
      const d = res.data?.data || res.data || {};
      const imported = d.imported ?? d.newTransactions ?? null;
      return {
        message: imported != null
          ? `Sync complete — ${imported} new transaction${imported === 1 ? '' : 's'} imported.`
          : 'Interactive Brokers sync complete.'
      };
    }
  }
};

/** Run an offered action by key. Unknown keys throw (the UI never renders them). */
export async function executeKcAction(key, params) {
  const def = KC_ACTION_EXECUTORS[key];
  if (!def) throw new Error(`Unknown action: ${key}`);
  return def.execute(params);
}

/** Reverse a completed action using the undoToken its executor returned. */
export async function undoKcAction(key, undoToken) {
  const def = KC_ACTION_EXECUTORS[key];
  if (!def?.undo || !undoToken) throw new Error('This action cannot be undone.');
  return def.undo(undoToken);
}

export const KC_ACTION_KEYS = Object.keys(KC_ACTION_EXECUTORS);
